import React, { useCallback, useId, useRef, useState } from 'react'
import { useFileUpload } from '../../DataMapper/extra/useDataMapper'
import { ALLOWED_EXTENSIONS } from '../extra/constants'
import '../../../../assets/css/style.css'
import '../../../../assets/css/common.css'

/**
 * @param {File[]} files
 * @returns {File[]}
 */
const filterFilesByExtension = (files) => {
  return Array.from(files).filter((file) => {
    const ext = String(file.name).split('.').pop()?.toLowerCase() || ''
    return ALLOWED_EXTENSIONS.includes(ext)
  })
}

/**
 * @typedef {Object} FileUploadAreaProps
 * @property {function(File[]): void} onFilesSelected
 * @property {boolean} disabled
 */

/**
 * FileUploadAreaコンポーネント
 *
 * @param {FileUploadAreaProps} props
 */
const FileUploadArea = ({ onFilesSelected, disabled = false }) => {
  const { handleFileDragOver } = useFileUpload()
  const fileInputRef = useRef(null)
  const fileInputId = useId()

  const handleFileSelection = useCallback(
    (fileElement) => {
      if (!fileElement?.files?.length) return
      const files = filterFilesByExtension(Array.from(fileElement.files))
      if (files.length > 0) {
        onFilesSelected(files)
      }
      fileElement.value = ''
    },
    [onFilesSelected]
  )

  const [isDragover, setIsDragover] = useState(false)

  const handleDragOverInternal = useCallback(
    (e) => {
      e.preventDefault()
      if (!disabled) {
        setIsDragover(true)
        handleFileDragOver(e)
      }
    },
    [handleFileDragOver, disabled]
  )

  const handleDragLeave = useCallback((e) => {
    e.preventDefault()
    setIsDragover(false)
  }, [])

  const handleDropInternal = useCallback(
    (e) => {
      e.preventDefault()
      setIsDragover(false)
      if (disabled) return
      const files = e.dataTransfer?.files
      if (!files?.length) return
      const filtered = filterFilesByExtension(Array.from(files))
      if (filtered.length > 0) {
        onFilesSelected(filtered)
      }
    },
    [onFilesSelected, disabled]
  )

  const dropSurfaceClass = `border-2 border-dashed rounded-xl p-5 transition-all ${
    isDragover
      ? 'border-blue2 bg-blue2-lighten-50'
      : 'border-gray-200 bg-gray-50 hover:border-gray-300 hover:bg-gray-100'
  }`

  /** アップロード画面モックアップに合わせた主ボタン色（#3182ce）。ラベル内でも背景が消えないよう明示指定 */
  const primaryPickClass =
    'btn inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-md px-6 py-2.5 text-sm font-medium text-white !bg-[#3182ce] !border-0 hover:!bg-[#2b6cb0]'

  const inner = (
    <>
      <input
        id={fileInputId}
        type="file"
        ref={fileInputRef}
        multiple
        accept={ALLOWED_EXTENSIONS.map((ext) => `.${ext}`).join(',')}
        className="sr-only"
        disabled={disabled}
        onChange={(e) => handleFileSelection(e.target)}
      />
      <div className="relative z-[1] flex min-h-[72px] flex-wrap items-center justify-center gap-6 sm:gap-8">
        <div className="shrink-0 text-4xl" aria-hidden>
          📄
        </div>
        <div className="min-w-0 max-w-[min(100%,30rem)] text-left">
          <p className="text-body mb-1 font-semibold text-gray-900">
            ファイルをドラッグ＆ドロップ、またはボタンから選択
          </p>
          <p className="text-caption-2 mb-0 text-gray-600">複数ファイルを同時にアップロードできます</p>
        </div>
        <span className={primaryPickClass}>ファイルを選択</span>
      </div>
    </>
  )

  if (disabled) {
    return <div className={`${dropSurfaceClass} cursor-not-allowed opacity-50`}>{inner}</div>
  }

  return (
    <label
      htmlFor={fileInputId}
      className={`${dropSurfaceClass} block cursor-pointer`}
      onDrop={handleDropInternal}
      onDragOver={handleDragOverInternal}
      onDragLeave={handleDragLeave}
    >
      {inner}
    </label>
  )
}

export default FileUploadArea
